import React, { useState, useEffect } from 'react';
import { ShieldCheck, Activity, ShieldAlert } from 'lucide-react';
import { cn } from '../../lib/utils';

export const StabilityManager: React.FC = () => {
  const [status, setStatus] = useState<'healthy' | 'warning' | 'error'>('healthy');
  const [logs, setLogs] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      setStatus('warning');
      setLogs(prev => [...prev.slice(-4), `Error: ${event.message}`]);
      
      // Auto-resolve common minor issues
      if (event.message.includes('Script error')) {
        console.log('StabilityManager: Suppressing cross-origin script error.');
      }
    };

    window.addEventListener('error', handleGlobalError);
    return () => window.removeEventListener('error', handleGlobalError);
  }, []);

  // Simple heartbeat
  useEffect(() => {
    const interval = setInterval(() => {
      // Logic to check connectivity or critical state could go here
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* Invisible trigger in the bottom left */}
      <div 
        className="fixed bottom-0 left-0 w-8 h-8 z-[9999] cursor-help"
        onMouseEnter={() => setIsVisible(true)}
        onClick={() => setIsVisible(!isVisible)}
      />

      <div className={cn(
        "fixed bottom-6 left-6 z-[9999] transition-all duration-500",
        isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0 pointer-events-none"
      )}>
        <div 
          className="bg-white/90 backdrop-blur-xl border border-slate-100 p-4 rounded-[2rem] shadow-2xl flex items-center gap-4 cursor-default"
          onMouseLeave={() => setIsVisible(false)}
        >
          <div className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
            status === 'healthy' ? "bg-emerald-50 text-emerald-500" : 
            status === 'warning' ? "bg-amber-50 text-amber-500" : "bg-red-50 text-red-500"
          )}>
            {status === 'healthy' ? <ShieldCheck size={20} /> : <ShieldAlert size={20} />}
          </div>
          
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-900">System Integrity</span>
              <Activity size={10} className="text-indigo-500 animate-pulse" />
            </div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
              {status === 'healthy' ? 'Protected by Anti-Error Logic' : 'Investigating Disruptions'}
            </p>
          </div>

          {logs.length > 0 && (
            <div className="ml-4 pl-4 border-l border-slate-100">
              <p className="text-[8px] font-mono text-slate-400 uppercase">Recent Diagnostics:</p>
              {logs.map((log, i) => (
                <p key={i} className="text-[8px] font-mono text-slate-600 truncate max-w-[150px]">{log}</p>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
